import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorMessage } from "./ErrorMessage";

type State = { error: unknown };

/** A page's code that failed to load (a new deployment, or the dev server re-bundling): only a reload fetches it. */
export function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      error.message,
    )
  );
}

/**
 * Catches a page that throws while rendering, so the frame and its navigation stay usable. Moving to another page
 * (a new `resetKey`) clears the error; Try again renders the page again, or reloads when its code failed to load,
 * since a lazy page keeps the failed import.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, State> {
  state: State = { error: undefined };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey?: string }): void {
    if (this.state.error !== undefined && prev.resetKey !== this.props.resetKey) this.setState({ error: undefined });
  }

  render() {
    const { error } = this.state;
    if (error !== undefined) {
      const retry = isChunkLoadError(error)
        ? () => window.location.reload()
        : () => this.setState({ error: undefined });
      return <ErrorMessage error={error} onRetry={retry} technical />;
    }
    return this.props.children;
  }
}
